import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNewsletterEditor } from "../context/NewsletterEditorContext";
import { buildCellNumberMap } from "../model/cellIndexUtils";
import { getColumnDropHighlight, getColumnSlotState } from "../model/columnContentUtils";
import { buildCellInnerCanvasStyle } from "../model/cellStyleUtils";
import { buildLayoutInnerCanvasStyle } from "../model/layoutInnerStyleUtils";
import {
  cellComponentSlotSx,
  cellInnerContentSx,
  cellInnerLayoutSx,
  cellShellSx,
  cellTableShellSx,
} from "../model/cellCanvasUtils";
import { layoutBlockOuterSx } from "../utils/layoutStyleUtils";
import { layoutGridTableCanvasSx, layoutGridRowCanvasSx, nestedLayoutBlockCanvasSx, nestedLayoutInnerCanvasSx } from "../model/layoutCanvasUtils";
import {
  CELL_DROP_INVALID_OUTLINE,
  CELL_DROP_OK_OUTLINE,
  CELL_LABEL_OPACITY,
  EDITOR_ACCENT,
  LAYOUT_HEADER_BG,
} from "../editorUiConstants";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import ComponentVisualPreview from "./ComponentVisualPreview";
import CellEmptyPlaceholder from "./CellEmptyPlaceholder";
import { DRAG_TYPE, DRAG_TYPE_IMAGE, DRAG_TYPE_LAYOUT } from "./LeftPanel";
import { buildImageComponentPropsFromLibrary } from "../model/defaultComponents";

const isLibraryDragEvent = (event) => {
  const types = Array.from(event.dataTransfer?.types || []);
  return (
    types.includes(DRAG_TYPE) ||
    types.includes(`${DRAG_TYPE}/saved`) ||
    types.includes(`${DRAG_TYPE_LAYOUT}/saved`) ||
    types.includes(DRAG_TYPE_IMAGE)
  );
};

const CellLabelHandle = ({ cellNumber, visible, selected, onClick }) => (
  <Box
    component="button"
    type="button"
    className={`wcp-cell-label${selected ? " wcp-cell-label--selected" : ""}`}
    aria-label={`Cella ${cellNumber}`}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{
      position: "absolute",
      top: 3,
      left: 3,
      zIndex: 12,
      px: 0.875,
      py: 0.375,
      border: "none",
      borderRadius: "3px",
      cursor: "pointer",
      backgroundColor: selected ? EDITOR_ACCENT : "#64748b",
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 1.25,
      fontFamily: "inherit",
      opacity: selected
        ? CELL_LABEL_OPACITY.selected
        : visible
          ? CELL_LABEL_OPACITY.hovered
          : CELL_LABEL_OPACITY.hidden,
      transition: "opacity 0.12s, background-color 0.12s",
      pointerEvents: visible || selected ? "auto" : "none",
      boxShadow: "0 1px 4px rgba(15, 23, 42, 0.18)",
    }}
  >
    Cella {cellNumber}
  </Box>
);

CellLabelHandle.propTypes = {
  cellNumber: PropTypes.number.isRequired,
  visible: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

const LayoutBlockHeader = ({ grid, selected, hovered, onSelect, onDeleteRequest }) => {
  const displayName = grid.name?.trim() || "Layout";
  const headerBg = selected
    ? LAYOUT_HEADER_BG.selected
    : hovered
      ? LAYOUT_HEADER_BG.hovered
      : LAYOUT_HEADER_BG.default;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      sx={{
        px: 0.75,
        py: 0.25,
        height: 16,
        backgroundColor: headerBg,
        borderBottom: "1px solid #e2e8f0",
        cursor: "pointer",
      }}
    >
      <Typography
        variant="caption"
        noWrap
        sx={{
          fontWeight: selected ? 700 : 600,
          fontSize: 11,
          lineHeight: 1.2,
          color: selected ? EDITOR_ACCENT : "#64748b",
          maxWidth: selected ? "calc(100% - 28px)" : "100%",
        }}
      >
        {displayName}
      </Typography>
      {selected ? (
        <IconButton
          size="small"
          aria-label="Elimina layout"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteRequest();
          }}
          sx={{ width: 22, height: 22, color: "#b30047", flexShrink: 0 }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      ) : null}
    </Stack>
  );
};

LayoutBlockHeader.propTypes = {
  grid: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  hovered: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onDeleteRequest: PropTypes.func.isRequired,
};

const ColumnResizeHandle = ({ rowId, leftColumn, rightColumn, positionPercent, onResize }) => {
  const dragging = useRef(false);

  const handleMouseDown = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      dragging.current = true;
      const startX = event.clientX;
      const gridElement = event.currentTarget.parentElement;
      const gridWidth = gridElement?.offsetWidth || 1;
      const startLeft = leftColumn.widthPercent;
      const startRight = rightColumn.widthPercent;

      const handleMouseMove = (moveEvent) => {
        if (!dragging.current) {
          return;
        }
        const deltaPercent = ((moveEvent.clientX - startX) / gridWidth) * 100;
        let nextLeft = startLeft + deltaPercent;
        let nextRight = startRight - deltaPercent;
        if (nextLeft < 10) {
          nextRight -= 10 - nextLeft;
          nextLeft = 10;
        }
        if (nextRight < 10) {
          nextLeft -= 10 - nextRight;
          nextRight = 10;
        }
        onResize(rowId, leftColumn.id, rightColumn.id, nextLeft, nextRight);
      };

      const handleMouseUp = () => {
        dragging.current = false;
        globalThis.removeEventListener("mousemove", handleMouseMove);
        globalThis.removeEventListener("mouseup", handleMouseUp);
      };

      globalThis.addEventListener("mousemove", handleMouseMove);
      globalThis.addEventListener("mouseup", handleMouseUp);
    },
    [leftColumn, rightColumn, onResize, rowId]
  );

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: `calc(${positionPercent}% - 4px)`,
        width: 8,
        cursor: "col-resize",
        pointerEvents: "auto",
        backgroundColor: "transparent",
        "&:hover": { backgroundColor: "rgba(11, 61, 135, 0.35)" },
      }}
      aria-label="Ridimensiona colonne"
    />
  );
};

ColumnResizeHandle.propTypes = {
  rowId: PropTypes.string.isRequired,
  leftColumn: PropTypes.object.isRequired,
  rightColumn: PropTypes.object.isRequired,
  positionPercent: PropTypes.number.isRequired,
  onResize: PropTypes.func.isRequired,
};

const GridColumnResizeOverlay = ({ sampleRow, onResize }) => {
  const columns = sampleRow?.columns || [];
  if (!sampleRow || columns.length < 2) {
    return null;
  }

  let cumulative = 0;
  const boundaries = columns.slice(0, -1).map((leftColumn, index) => {
    const leftPercent = leftColumn.widthPercent ?? 100 / columns.length;
    cumulative += leftPercent;
    return {
      key: leftColumn.id,
      leftColumn,
      rightColumn: columns[index + 1],
      positionPercent: cumulative,
    };
  });

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 25,
      }}
    >
      {boundaries.map(({ key, leftColumn, rightColumn, positionPercent }) => (
        <ColumnResizeHandle
          key={key}
          rowId={sampleRow.id}
          leftColumn={leftColumn}
          rightColumn={rightColumn}
          positionPercent={positionPercent}
          onResize={onResize}
        />
      ))}
    </Box>
  );
};

GridColumnResizeOverlay.propTypes = {
  sampleRow: PropTypes.object,
  onResize: PropTypes.func.isRequired,
};

const renderLayoutBlock = (
  grid,
  selection,
  hoveredId,
  dragOverColumnId,
  setHoveredId,
  setDragOverColumnId,
  onSelect,
  onAttemptDrop,
  onAttemptLayoutDrop,
  onResize,
  onDeleteLayout,
  { isTopLevel = false } = {}
) => {
  const isGridSelected = selection?.type === "grid" && selection?.id === grid.id;
  const isGridHovered = hoveredId === grid.id;
  const cellNumberMap = buildCellNumberMap(grid);

  const table = (
    <Box
      component="table"
      sx={layoutGridTableCanvasSx}
    >
      <Box component="tbody">
        {(grid.rows || []).map((row) => (
          <Box component="tr" key={row.id} data-row-id={row.id} sx={layoutGridRowCanvasSx}>
            {(row.columns || []).map((column, index) => {
              const isColSelected = selection?.type === "column" && selection?.id === column.id;
              const isColHovered = hoveredId === column.id;
              const isDragTarget = dragOverColumnId === column.id;
              const slot = getColumnSlotState(column);
              const dropHighlight = isDragTarget ? getColumnDropHighlight(column, true) : null;
              const chromeOutline =
                dropHighlight === "ok"
                  ? CELL_DROP_OK_OUTLINE
                  : dropHighlight === "invalid"
                    ? CELL_DROP_INVALID_OUTLINE
                    : undefined;
              const cellGap = grid.props?.cellGap ?? 0;

              return (
                <Box
                  component="td"
                  key={column.id}
                  data-column-id={column.id}
                  sx={{
                    ...cellTableShellSx,
                    width: `${column.widthPercent || 100}%`,
                  }}
                  onDragEnter={(e) => {
                    if (!isLibraryDragEvent(e)) {
                      return;
                    }
                    e.preventDefault();
                    setDragOverColumnId(column.id);
                  }}
                  onDragOver={(e) => {
                    if (!isLibraryDragEvent(e)) {
                      return;
                    }
                    e.preventDefault();
                    setDragOverColumnId(column.id);
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget)) {
                      return;
                    }
                    setDragOverColumnId((current) => (current === column.id ? null : current));
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverColumnId(null);
                    const savedLayout = e.dataTransfer.getData(`${DRAG_TYPE_LAYOUT}/saved`);
                    if (savedLayout) {
                      try {
                        onAttemptLayoutDrop(column.id, JSON.parse(savedLayout));
                      } catch {
                        return;
                      }
                      return;
                    }
                    const imagePayload = e.dataTransfer.getData(DRAG_TYPE_IMAGE);
                    if (imagePayload) {
                      try {
                        const image = JSON.parse(imagePayload);
                        onAttemptDrop(
                          column.id,
                          "image",
                          buildImageComponentPropsFromLibrary(image, image.displayName)
                        );
                      } catch {
                        return;
                      }
                      return;
                    }
                    const savedComponent = e.dataTransfer.getData(`${DRAG_TYPE}/saved`);
                    if (savedComponent) {
                      try {
                        const saved = JSON.parse(savedComponent);
                        onAttemptDrop(column.id, saved.componentKey, saved.props);
                      } catch {
                        return;
                      }
                      return;
                    }
                    const componentKey = e.dataTransfer.getData(DRAG_TYPE);
                    if (componentKey) {
                      onAttemptDrop(column.id, componentKey, null);
                    }
                  }}
                  onMouseOver={(e) => {
                    e.stopPropagation();
                    setHoveredId(column.id);
                  }}
                  onMouseLeave={() => {
                    setHoveredId((current) => (current === column.id ? null : current));
                    setDragOverColumnId((current) => (current === column.id ? null : current));
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect({ type: "column", id: column.id, rowId: row.id, gridId: grid.id });
                  }}
                >
                  <CellLabelHandle
                    cellNumber={cellNumberMap.get(column.id)}
                    visible={isColHovered || isDragTarget}
                    selected={isColSelected}
                    onClick={() => onSelect({ type: "column", id: column.id, rowId: row.id, gridId: grid.id })}
                  />
                  <Box sx={cellShellSx(cellGap, chromeOutline)}>
                    <Box
                      style={buildCellInnerCanvasStyle(column.props)}
                      sx={cellInnerLayoutSx(column.props)}
                    >
                      <Box sx={cellInnerContentSx}>
                        {slot.kind === "empty" ? <CellEmptyPlaceholder /> : null}
                        {slot.kind === "nestedGrid" ? (
                          <Box sx={cellComponentSlotSx}>
                            {renderLayoutBlock(
                              slot.nestedGrid,
                              selection,
                              hoveredId,
                              dragOverColumnId,
                              setHoveredId,
                              setDragOverColumnId,
                              onSelect,
                              onAttemptDrop,
                              onAttemptLayoutDrop,
                              onResize,
                              onDeleteLayout
                            )}
                          </Box>
                        ) : null}
                        {slot.component ? (
                          <Box sx={cellComponentSlotSx}>
                            <ComponentVisualPreview
                              key={slot.component.id}
                              component={slot.component}
                              onSelect={onSelect}
                            />
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      key={grid.id}
      onMouseEnter={() => setHoveredId(grid.id)}
      onMouseLeave={() => setHoveredId((current) => (current === grid.id ? null : current))}
      sx={{
        mb: isTopLevel ? 1.5 : 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        ...(isTopLevel ? {} : nestedLayoutBlockCanvasSx),
      }}
    >
      <Box sx={{ ...(isTopLevel ? {} : { flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }), ...layoutBlockOuterSx(grid.props) }}>
        <LayoutBlockHeader
          grid={grid}
          selected={isGridSelected}
          hovered={isGridHovered}
          onSelect={() => onSelect({ type: "grid", id: grid.id })}
          onDeleteRequest={() => onDeleteLayout(grid.id)}
        />
        <Box style={buildLayoutInnerCanvasStyle(grid.props)} sx={isTopLevel ? undefined : nestedLayoutInnerCanvasSx}>
          <Box sx={{ position: "relative", width: "100%" }}>
            {table}
            <GridColumnResizeOverlay sampleRow={grid.rows?.[0]} onResize={onResize} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const StructureCanvas = () => {
  const {
    document,
    selection,
    setSelection,
    insertComponent,
    insertLayoutInColumn,
    resizeColumns,
    removeSelected,
    showEditorMessage,
  } = useNewsletterEditor();

  const [hoveredId, setHoveredId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  const [deleteLayoutId, setDeleteLayoutId] = useState(null);
  const [replaceConfirm, setReplaceConfirm] = useState(null);

  const runInsert = (columnId, componentKey, propsOverride = null, replace = false) => {
    const result = insertComponent(columnId, componentKey, propsOverride, { replace });

    if (result === "blocked_nested_grid") {
      showEditorMessage({
        title: "Cella non disponibile",
        message: "Questa cella contiene un layout interno.",
        detail: "Rimuovi il layout interno prima di inserire un componente.",
      });
      return;
    }
    if (result === "blocked") {
      setReplaceConfirm({ columnId, componentKey, propsOverride, kind: "component" });
    }
  };

  const runInsertLayout = (columnId, savedGrid, replace = false) => {
    const result = insertLayoutInColumn(columnId, savedGrid, { replace });
    if (result === "blocked") {
      setReplaceConfirm({ columnId, savedLayout: savedGrid, kind: "layout" });
    }
  };

  const handleAttemptLayoutDrop = (columnId, savedGrid) => {
    runInsertLayout(columnId, savedGrid, false);
  };

  const handleAttemptDrop = (columnId, componentKey, propsOverride = null) => {
    runInsert(columnId, componentKey, propsOverride, false);
  };

  const layouts = document.layout?.grids || [];
  const isEmpty = layouts.length === 0;

  return (
    <Box onClick={() => setSelection(null)} sx={{ height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fff" }}>
      <Box sx={{ flex: 1, overflow: "auto", p: isEmpty ? 0 : 1, display: "flex", flexDirection: "column" }}>
        {isEmpty ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Nessun layout nella email. Usa «Aggiungi nuovo layout» nel pannello Layout a sinistra.
            </Typography>
          </Box>
        ) : (
          layouts.map((grid) =>
            renderLayoutBlock(
              grid,
              selection,
              hoveredId,
              dragOverColumnId,
              setHoveredId,
              setDragOverColumnId,
              setSelection,
              handleAttemptDrop,
              handleAttemptLayoutDrop,
              resizeColumns,
              (gridId) => setDeleteLayoutId(gridId),
              { isTopLevel: true }
            )
          )
        )}
      </Box>

      <ConfirmDialog
        open={Boolean(deleteLayoutId)}
        onClose={() => setDeleteLayoutId(null)}
        onConfirm={() => {
          removeSelected(deleteLayoutId);
          setDeleteLayoutId(null);
        }}
        title="Elimina layout"
        message="Eliminare questo layout e tutto il suo contenuto?"
        confirmColor="#b30047"
      />

      <ConfirmDialog
        open={Boolean(replaceConfirm)}
        onClose={() => setReplaceConfirm(null)}
        onConfirm={() => {
          const pending = replaceConfirm;
          setReplaceConfirm(null);
          if (!pending) {
            return;
          }
          if (pending.kind === "layout") {
            runInsertLayout(pending.columnId, pending.savedLayout, true);
            return;
          }
          runInsert(pending.columnId, pending.componentKey, pending.propsOverride, true);
        }}
        title={replaceConfirm?.kind === "layout" ? "Sostituire il contenuto?" : "Sostituire il componente?"}
        message={
          replaceConfirm?.kind === "layout"
            ? "La cella contiene già un contenuto. Sostituirlo con il layout trascinato?"
            : "La cella contiene già un componente. Sostituirlo con quello nuovo?"
        }
        confirmColor="#0b3d87"
      />
    </Box>
  );
};

export default StructureCanvas;
 